import { RefObject } from 'preact';
import { useModal } from './hooks/useModal';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { Layer } from '../UNSAFE_Layer';
import ModalBackdrop from './ModalBackdrop';
import ModalContainer from './ModalContainer';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;
type UseModalProps = Parameters<typeof useModal>[0];
type Props = UseModalProps &
  IntrinsicProps & {
    /**
     * Optional Ref to an element to focus on when component mounts
     * @default - undefined - behavior is to focus on first tabbableElement
     */
    autoFocusRef?: RefObject<HTMLElement>;

    /**
     * Optional Ref to element to focus on when component un-mounts
     * @default - undefined - behavior is to focus on the original activeElement when focus trap was enabled
     */
    restoreFocusRef?: RefObject<HTMLElement>;
  };

const styles = {
  base: cssProps`${{
    position: 'fixed',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0'
  }}`
};

const ModalWrapper = ({ children }: IntrinsicProps) => <div class={styles.base}>{children}</div>;

const Modal = ({ autoFocusRef, children, isOpen, onBackdropClick, restoreFocusRef }: Props) => {
  const { modalRef } = useModal({ isOpen, onBackdropClick });

  return isOpen ? (
    <Layer>
      <ModalWrapper>
        <ModalBackdrop />
        <ModalContainer ref={modalRef}>
          <FocusTrap autoFocusRef={autoFocusRef} restoreFocusRef={restoreFocusRef}>
            {children}
          </FocusTrap>
        </ModalContainer>
      </ModalWrapper>
    </Layer>
  ) : null;
};

export default Modal;
